import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import LanguagePicker from '@/components/ui/LanguagePicker'
import { ModuleMilestone } from '@/components/education/ModuleMilestone'
import SafeVideoPlayer from '@/components/video/SafeVideoPlayer'
import { ClipboardCheck, X, CheckCircle2, Trophy, Clock, HelpCircle, ChevronRight, Lock, Sparkles } from 'lucide-react'
import { ShortAnswerBox } from '@/components/education/ShortAnswerBox'
import { isFreeFaithCategory } from '@/config/curriculum'
import { getEntitlements } from '@/utils/entitlements'
import { generateReflectionPrompt } from '@/utils/reflectionPrompt'

// ─── Design tokens (church brand, matches ChurchMemberPage) ────────────────────
const GOLD = '#c8a84b'
const DARK = '#0f1a2b'
const DARK2 = '#0a1220'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.4)'

const ChurchIcon = ({ size = 32 }: { size?: number }) => (
    <div style={{
        width: size, height: size, background: '#0ea5e9', borderRadius: size * 0.28,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(14,165,233,0.2)'
    }}>
        <svg width={size * 0.45} height={size * 0.55} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M7 10H17" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </svg>
    </div>
)

// ─── Types ─────────────────────────────────────────────────────────────────────
interface LessonMeta {
    lesson_id: number
    title: string
    learning_objective?: string
    core_concepts?: string[]
}
interface ModuleMeta {
    module_id: number
    title: string
    lessons: LessonMeta[]
}
interface ClassInfo {
    id: string
    name: string
    category_id: string
    module_plan: ModuleMeta[]
    description?: string | null
    essay_mode?: string | null
}

interface EduMessage {
    id: string
    role: 'user' | 'assistant'
    content: string
    created_at: string
    metadata?: Record<string, any>
}

interface EduUpdate {
    lesson_complete?: boolean
    new_current_lesson?: number
    trigger_quiz?: boolean
    current_concept_index?: number
    suggested_chips?: string[]
}

interface QuizQuestion {
    id: string
    question: string
    options: string[]
    correct_index: number
}

interface Quiz {
    questions: QuizQuestion[]
}

// ─── Strip EDU_UPDATE artefacts from display text ────────────────────────────
function cleanEduContent(raw: string): string {
    return raw
        .replace(/EDU_UPDATE::\{[^}]*\}/g, '')
        .replace(/EDU_UPDATE::/g, '')
        .replace(/EDU_UPDATE/g, '')
        .replace(/\bEDU\b/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trimEnd()
}

// ─── Markdown components (dark theology-themed) ───────────────────────────────
const mdComponents = {
    p: ({ children, ...p }: any) => (
        <p {...p} style={{ fontSize: 15, lineHeight: 1.75, marginBottom: '0.9em', color: TX }}>{children}</p>
    ),
    strong: ({ children, ...p }: any) => (
        <strong {...p} style={{ fontWeight: 700, color: '#fff' }}>{children}</strong>
    ),
    em: ({ children, ...p }: any) => (
        <em {...p} style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.8)' }}>{children}</em>
    ),
    h1: ({ children, ...p }: any) => (
        <h1 {...p} style={{ fontSize: 22, fontWeight: 800, color: GOLD, marginBottom: '0.5em', marginTop: '1em', borderBottom: `1px solid ${BR}`, paddingBottom: '0.3em', letterSpacing: '-0.3px' }}>{children}</h1>
    ),
    h2: ({ children, ...p }: any) => (
        <h2 {...p} style={{ fontSize: 18, fontWeight: 700, color: '#f0e6cf', marginBottom: '0.4em', marginTop: '0.8em' }}>{children}</h2>
    ),
    h3: ({ children, ...p }: any) => (
        <h3 {...p} style={{ fontSize: 16, fontWeight: 700, color: TX, marginBottom: '0.35em', marginTop: '0.7em' }}>{children}</h3>
    ),
    ul: ({ children, ...p }: any) => (
        <ul {...p} style={{ paddingLeft: '1.2em', marginBottom: '0.8em', listStyleType: 'none' }}>{children}</ul>
    ),
    ol: ({ children, ...p }: any) => (
        <ol {...p} style={{ paddingLeft: '1.4em', marginBottom: '0.8em', color: MU }}>{children}</ol>
    ),
    li: ({ children, ...p }: any) => (
        <li {...p} style={{ position: 'relative', fontSize: 15, lineHeight: 1.7, color: TX, marginBottom: '0.4em' }}>
            {!p.ordered && <span style={{ position: 'absolute', left: '-1em', color: GOLD }}>•</span>}
            {children}
        </li>
    ),
    blockquote: ({ children, ...p }: any) => (
        <blockquote {...p} style={{ borderLeft: `4px solid ${GOLD}`, padding: '12px 18px', margin: '1.2em 0', background: 'rgba(200,168,75,0.05)', borderRadius: '0 10px 10px 0', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>{children}</blockquote>
    ),
    code: ({ children, className, ...p }: any) => {
        const isInline = !className
        return isInline
            ? <code {...p} style={{ background: 'rgba(200,168,75,0.13)', color: '#f5e0a0', padding: '2px 5px', borderRadius: 4, fontSize: '0.9em' }}>{children}</code>
            : <code {...p} style={{ display: 'block', background: 'rgba(0,0,0,0.35)', color: '#e2dcca', padding: 16, borderRadius: 12, fontSize: 13, overflowX: 'auto', lineHeight: 1.5 }}>{children}</code>
    },
    a: ({ children, ...p }: any) => (
        <a {...p} style={{ color: GOLD, textDecoration: 'none', borderBottom: `1px solid ${GOLD}` }} target="_blank" rel="noreferrer">{children}</a>
    ),
}

// ─── Typing dots ───────────────────────────────────────────────────────────────
function TypingDots() {
    return (
        <div style={{ display: 'flex', gap: 5, padding: '14px 18px', alignItems: 'center' }}>
            {[0, 1, 2].map(i => (
                <motion.div key={i} animate={{ y: [0, -5, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                    style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD }} />
            ))}
        </div>
    )
}

// ─── Media Parser ──────────────────────────────────────────────────────────────
function parseMediaContent(content: string, isMobile: boolean): React.ReactNode[] {
    const parts: React.ReactNode[] = []
    const regex = /\[(IMAGE|VIDEO):\s*([^\]|]+?)(?:\s*\|\s*(?:Caption:\s*"?)?([^"\]]*)?"?)?\]/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            parts.push(
                <ReactMarkdown key={`t-${lastIndex}`} remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {content.slice(lastIndex, match.index)}
                </ReactMarkdown>
            )
        }
        const type = match[1]
        const url = match[2].trim()
        const caption = match[3]?.trim() || ''
        if (type === 'IMAGE') {
            parts.push(
                <div key={`img-${match.index}`} style={{
                    margin: '16px 0', borderRadius: 14, overflow: 'hidden',
                    border: `1px solid ${BR}`, background: CA
                }}>
                    <img src={url} alt={caption || 'Lesson visual'} style={{
                        width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block'
                    }} loading="lazy" />
                    {caption && (
                        <p style={{ padding: '10px 14px', fontSize: 12, color: MU, margin: 0, fontStyle: 'italic' }}>
                            {caption}
                        </p>
                    )}
                </div>
            )
        } else if (type === 'VIDEO') {
            parts.push(
                <div key={`vid-${match.index}`} style={{ margin: '16px 0', borderRadius: 14, overflow: 'hidden' }}>
                    <SafeVideoPlayer videoUrl={url} title={caption || 'Lesson video'} className="w-full aspect-video" />
                </div>
            )
        }
        lastIndex = match.index + match[0].length
    }

    if (lastIndex < content.length) {
        parts.push(
            <ReactMarkdown key="t-end" remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content.slice(lastIndex)}
            </ReactMarkdown>
        )
    }

    return parts.length > 0 ? parts : [<ReactMarkdown key="full" remarkPlugins={[remarkGfm]} components={mdComponents}>{content}</ReactMarkdown>]
}

// ─── Message Bubble ────────────────────────────────────────────────────────────
function MessageBubble({ content, role, isMobile }: { content: string; role: 'user' | 'assistant'; isMobile: boolean }) {
    const cleaned = role === 'assistant' ? cleanEduContent(content) : content
    const hasMedia = role === 'assistant' && /\[(IMAGE|VIDEO):/.test(cleaned)

    return (
        <div style={{ display: 'flex', justifyContent: role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 18, gap: isMobile ? 6 : 10, alignItems: 'flex-start' }}>
            {role === 'assistant' && (
                <div style={{ marginTop: 4 }}><ChurchIcon size={isMobile ? 24 : 32} /></div>
            )}
            <div style={{ maxWidth: isMobile ? '94%' : '76%', padding: isMobile ? '10px 10px' : '14px 18px', borderRadius: role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px', background: role === 'user' ? `linear-gradient(135deg, ${GOLD}, #9a7228)` : CA, border: role === 'user' ? 'none' : `1px solid ${BR}`, boxShadow: role === 'user' ? `0 4px 14px rgba(200,168,75,0.2)` : '0 2px 10px rgba(0,0,0,0.12)' }}>
                {role === 'assistant' ? (
                    <div className="prose-church">
                        {hasMedia ? parseMediaContent(cleaned, isMobile) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{cleaned}</ReactMarkdown>
                        )}
                    </div>
                ) : (
                    <p style={{ fontSize: 14, lineHeight: 1.6, color: '#fff', whiteSpace: 'pre-wrap', margin: 0, fontWeight: 500 }}>{cleaned}</p>
                )}
            </div>
        </div>
    )
}

// ─── Quiz Panel ────────────────────────────────────────────────────────────────
function QuizPanel({ quiz, onSubmit, onDismiss }: { quiz: Quiz; onSubmit: (answers: Record<string, number>) => void; onDismiss: () => void }) {
    const { t } = useTranslation()
    const [answers, setAnswers] = useState<Record<string, number>>({})
    const allAnswered = quiz.questions.every(q => answers[q.id] !== undefined)
    const answeredCount = quiz.questions.filter(q => answers[q.id] !== undefined).length
    const progress = (answeredCount / quiz.questions.length) * 100

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: 'absolute',
                inset: 0,
                background: '#0a1220', // FULLY OPAQUE
                overflowY: 'auto',
                zIndex: 200,
                display: 'flex',
                flexDirection: 'column'
            }}
            className="scrollbar-hide"
        >
            {/* High-end Sticky Header */}
            <div style={{
                position: 'sticky',
                top: 0,
                background: 'rgba(10,18,32,0.9)',
                backdropFilter: 'blur(15px)',
                borderBottom: `1px solid ${BR}`,
                padding: '24px 24px 20px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 16
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 12, background: 'rgba(200,168,75,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: GOLD
                        }}>
                            <ClipboardCheck size={22} />
                        </div>
                        <div>
                            <p style={{ fontSize: 11, fontWeight: 900, color: GOLD, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 2 }}>
                                {t('church.session.quiz.title')}
                            </p>
                            <p style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.4px' }}>
                                {t('church.session.quiz.subtitle')}
                            </p>
                        </div>
                    </div>

                    <motion.button
                        whileHover={{ scale: 1.05, background: 'rgba(255,255,255,0.08)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onDismiss}
                        style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: `1px solid ${BR}`,
                            color: MU,
                            borderRadius: 12,
                            width: 40, height: 40,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={18} />
                    </motion.button>
                </div>

                {/* Progress bar */}
                <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 10, overflow: 'hidden' }}>
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        style={{ height: '100%', background: `linear-gradient(90deg, ${GOLD}, #f5d17a)`, boxShadow: `0 0 12px ${GOLD}44` }}
                    />
                </div>
            </div>

            <div style={{ maxWidth: 640, margin: '0 auto', width: '100%', padding: '32px 24px 80px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, padding: '0 8px' }}>
                    <Clock size={14} color={GOLD} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: MU }}>
                        {answeredCount} OF {quiz.questions.length} QUESTIONS ANSWERED
                    </span>
                </div>

                {quiz?.questions?.map((q, qi) => (
                    <motion.div
                        key={q.id || qi}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: qi * 0.08 }}
                        style={{
                            background: 'linear-gradient(165deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                            border: `1px solid ${answers[q.id] !== undefined ? 'rgba(200,168,75,0.25)' : BR}`,
                            borderRadius: 24,
                            padding: '24px',
                            marginBottom: 20,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.2)'
                        }}
                    >
                        <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: 8, background: answers[q.id] !== undefined ? GOLD : 'rgba(255,255,255,0.08)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                fontSize: 13, fontWeight: 900, color: answers[q.id] !== undefined ? DARK2 : MU
                            }}>
                                {qi + 1}
                            </div>
                            <p style={{ fontSize: 16, fontWeight: 700, color: TX, lineHeight: 1.5, marginTop: 2 }}>
                                {q.question}
                            </p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {Array.isArray(q.options) && q.options.map((opt, oi) => {
                                const isSelected = answers[q.id] === oi
                                return (
                                    <motion.button
                                        key={oi}
                                        whileHover={{ x: 4, background: isSelected ? 'rgba(200,168,75,0.18)' : 'rgba(255,255,255,0.05)' }}
                                        onClick={() => setAnswers(prev => ({ ...prev, [q.id]: oi }))}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            width: '100%',
                                            textAlign: 'left',
                                            background: isSelected ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.02)',
                                            border: `1px solid ${isSelected ? 'rgba(200,168,75,0.45)' : 'rgba(255,255,255,0.06)'}`,
                                            borderRadius: 14,
                                            padding: '14px 18px',
                                            color: isSelected ? '#fff' : TX,
                                            fontSize: 14,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            fontWeight: isSelected ? 700 : 500,
                                            boxShadow: isSelected ? `0 4px 15px ${GOLD}15` : 'none'
                                        }}
                                    >
                                        <div style={{
                                            width: 18, height: 18, borderRadius: '50%',
                                            border: `2px solid ${isSelected ? GOLD : 'rgba(255,255,255,0.15)'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: isSelected ? GOLD : 'transparent',
                                            flexShrink: 0
                                        }}>
                                            {isSelected && <CheckCircle2 size={12} color={DARK2} strokeWidth={4} />}
                                        </div>
                                        <span style={{ flex: 1 }}>{opt}</span>
                                    </motion.button>
                                )
                            })}
                        </div>
                    </motion.div>
                ))}

                <motion.button
                    whileHover={{ scale: allAnswered ? 1.02 : 1, y: allAnswered ? -2 : 0 }}
                    whileTap={{ scale: allAnswered ? 0.98 : 1 }}
                    onClick={() => allAnswered && onSubmit(answers)}
                    disabled={!allAnswered}
                    style={{
                        width: '100%',
                        padding: '18px',
                        background: allAnswered
                            ? `linear-gradient(135deg, ${GOLD}, #9a7228)`
                            : 'rgba(255,255,255,0.04)',
                        color: allAnswered ? DARK2 : MU,
                        border: 'none',
                        borderRadius: 18,
                        fontSize: 16,
                        fontWeight: 900,
                        letterSpacing: '0.5px',
                        cursor: allAnswered ? 'pointer' : 'not-allowed',
                        transition: 'all 0.3s',
                        boxShadow: allAnswered ? `0 12px 30px ${GOLD}35` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        marginTop: 20
                    }}
                >
                    {allAnswered ? (
                        <>
                            {t('church.session.quiz.submit')} <ChevronRight size={20} />
                        </>
                    ) : (
                        `PLEASE COMPLETE ALL QUESTIONS`
                    )}
                </motion.button>

                <p style={{ textAlign: 'center', color: MU, fontSize: 12, marginTop: 32, fontWeight: 500 }}>
                    <HelpCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    ALL QUESTIONS MUST BE ANSWERED TO SUBMIT
                </p>
            </div>
        </motion.div>
    )
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── ChurchSessionInner ───────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function ChurchSessionInner({ classId, classInfo, moduleId, lessonId, age, categoryId }: {
    classId: string,
    classInfo: ClassInfo,
    moduleId: number,
    lessonId: number,
    age?: number | null,
    categoryId: string
}) {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { t, i18n } = useTranslation()
    const { user, userProfile } = useAuth()
    const entitlements = getEntitlements(userProfile)
    const isFreeCourse = isFreeFaithCategory(classInfo.category_id)

    const mod = classInfo.module_plan.find(m => m.module_id === moduleId)
    const lesson = mod?.lessons.find(l => l.lesson_id === lessonId)
    const totalLessons = classInfo.module_plan.reduce((s, m) => s + (m.lessons?.length || 0), 0)

    // ── State ─────────────────────────────────────────────────────────────────
    const [messages, setMessages] = useState<EduMessage[]>([])
    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isStreaming, setIsStreaming] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sessionComplete, setSessionComplete] = useState(false)
    const [suggestedChips, setSuggestedChips] = useState<string[]>([])
    const [conversationId, setConversationId] = useState<string | null>(null)
    const [quiz, setQuiz] = useState<Quiz | null>(null)
    const [isQuizOpen, setIsQuizOpen] = useState(false)
    const [quizPassed, setQuizPassed] = useState(false)
    const [quizLoading, setQuizLoading] = useState(false)
    const [masteryScore, setMasteryScore] = useState(0)
    const [currentConceptIndex, setCurrentConceptIndex] = useState(0)
    const [completedLessons, setCompletedLessons] = useState<Record<string, boolean>>({})
    const [isMobile, setIsMobile] = useState(false)
    const [showSidebar, setShowSidebar] = useState(true)
    const [showMilestone, setShowMilestone] = useState(false)
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
    const [upgrading, setUpgrading] = useState(false)
    const [showReflection, setShowReflection] = useState(false)
    const [reflectionPrompt, setReflectionPrompt] = useState('')

    const refreshEnrolmentMastery = useCallback(async () => {
        if (!user?.id || !classId) return
        const { data } = await supabase
            .from('student_subject_progress')
            .select('mastery_score')
            .eq('user_id', user.id)
            .eq('class_id', classId)
            .maybeSingle()
        if (data?.mastery_score) setMasteryScore(data.mastery_score)
    }, [user?.id, classId])

    async function startCheckout() {
        setUpgrading(true)
        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) throw new Error('No session')

            const { data, error } = await supabase.functions.invoke('billing', {
                body: {
                    action: 'checkout',
                    plan: 'sage-church-seat',
                    interval: 'month',
                    quantity: 1,
                    destination: 'church_member',
                    facility_id: userProfile?.facility_id,
                },
                headers: { Authorization: `Bearer ${session.access_token}` },
            })
            if (error) throw error
            if (data?.url) window.location.href = data.url
        } catch (e) {
            console.error('Checkout error:', e)
            setError('Unable to start checkout. Please try again.')
        } finally {
            setUpgrading(false)
        }
    }

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const abortRef = useRef<AbortController | null>(null)
    const streamBufferRef = useRef('')
    const hasInitRef = useRef(false)
    const isLoadingRef = useRef(false)
    const isStreamingRef = useRef(false)

    // ── Responsive ───────────────────────────────────────────────────────────
    useEffect(() => {
        const check = () => {
            const mobile = window.innerWidth <= 820
            setIsMobile(mobile)
            if (!mobile) setShowSidebar(true)
        }
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    // ── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, isLoading])

    // ── Find category ID for church/bible courses ─────────────────────────────
    // Category resolution is now handled by the outer component

    // ── Load existing enrollment progress ─────────────────────────────────────
    useEffect(() => {
        if (!user || !classId) return
        supabase.from('class_enrollments').select('mastery_score, progress_state').eq('student_id', user.id).eq('class_id', classId).maybeSingle()
            .then(({ data }) => {
                if (data) {
                    setMasteryScore(data.mastery_score || 0)
                }
            })
        // Load completed lessons from enrollment progress_state
        supabase.from('class_enrollments').select('progress_state').eq('student_id', user.id).eq('class_id', classId).maybeSingle()
            .then(({ data }) => {
                if (data?.progress_state) {
                    const ps = data.progress_state as any
                    const done: Record<string, boolean> = {}
                    if (ps.completed_lessons) {
                        if (typeof ps.completed_lessons === 'object' && !Array.isArray(ps.completed_lessons)) {
                            // Object format from quiz-engine: {"1-1": true, "1-2": true}
                            Object.entries(ps.completed_lessons).forEach(([key, val]) => { if (val) done[key] = true })
                        } else if (Array.isArray(ps.completed_lessons)) {
                            ps.completed_lessons.forEach((cl: any) => { done[`${cl.module_id}-${cl.lesson_id}`] = true })
                        }
                    }
                    setCompletedLessons(done)
                }
            })
    }, [user, classId])

    // ── Handle ?reflection=1 direct link ─────────────────────────────────────
    useEffect(() => {
        if (searchParams.get('reflection') === '1' && classInfo?.essay_mode === 'essay_per_module' && user?.id) {
            supabase
                .from('university_submissions')
                .select('id')
                .eq('user_id', user.id)
                .eq('class_id', classId)
                .eq('module_id', moduleId)
                .maybeSingle()
                .then(({ data: sub }) => {
                    if (!sub) {
                        const prompt = generateReflectionPrompt({
                            mode: classInfo?.essay_mode || 'essay_per_module',
                            subjectName: classInfo?.name,
                            moduleTitle: mod?.title,
                            moduleNumber: moduleId,
                            lessonTitle: lesson?.title,
                            lessonNumber: lessonId,
                            coreConcepts: lesson?.core_concepts || [],
                            isFaithTrack: true,
                        })
                        setReflectionPrompt(prompt)
                        setShowReflection(true)
                    }
                })
        }
    }, [searchParams, classInfo?.essay_mode, classInfo?.name, mod?.title, moduleId, lesson?.title, lessonId, lesson?.core_concepts, user?.id, classId])

    // ── Initialise session ────────────────────────────────────────────────────
    const initialise = useCallback(async () => {
        if (!user || hasInitRef.current || isLoadingRef.current || !categoryId) return
        hasInitRef.current = true
        isLoadingRef.current = true
        setIsLoading(true)

        try {
            const { data: { session: authSession } } = await supabase.auth.getSession()
            if (!authSession?.access_token) throw new Error('Not authenticated')
            const supabaseUrl = (supabase as any).supabaseUrl as string

            // Find or create a conversation for this class+lesson
            const { data: existing } = await supabase.from('conversations')
                .select('id').eq('user_id', user.id)
                .contains('metadata', { class_id: classId, module_id: moduleId, lesson_id: lessonId, version: 2 })
                .order('created_at', { ascending: false }).limit(1).maybeSingle()

            let convId = existing?.id
            if (!convId) {
                const { data: newConv } = await (supabase as any).from('conversations')
                    .insert({ user_id: user.id, category_id: categoryId, title: `${classInfo.name} - Lesson ${lessonId}`, metadata: { class_id: classId, module_id: moduleId, lesson_id: lessonId, source: 'church', version: 2 } })
                    .select('id').single()
                convId = newConv?.id
            }

            if (!convId) throw new Error('Could not create session')
            setConversationId(convId)

            // Load session state (core concepts progress)
            const { data: convState } = await supabase.from('conversation_states').select('context_data').eq('conversation_id', convId).maybeSingle()
            if (convState?.context_data) {
                const cd = convState.context_data as any
                if (typeof cd.current_concept_index === 'number') {
                    setCurrentConceptIndex(cd.current_concept_index)
                }
            }

            // Load existing messages
            const { data: existingMsgs } = await supabase.from('messages').select('*')
                .eq('conversation_id', convId).order('created_at', { ascending: true }).limit(100)

            if (existingMsgs && existingMsgs.length > 0) {
                setMessages(existingMsgs.map(m => ({ id: m.id, role: m.role as 'user' | 'assistant', content: m.content || '', created_at: m.created_at ?? new Date().toISOString() })))
                setIsLoading(false)
                isLoadingRef.current = false
                return
            }

            // No existing messages — init with ai-education
            const initRes = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
                body: JSON.stringify({
                    conversation_id: convId,
                    message: '[init]',
                    category_id: categoryId,
                    class_id: classId,
                    session_type: 'education',
                    university_mode: false,
                    current_module: moduleId,
                    current_lesson: lessonId,
                    lesson_title: lesson?.title,
                    lesson_objective: lesson?.learning_objective,
                    core_concepts: lesson?.core_concepts,
                    user_age: age,
                    language: i18n.language,
                }),
            })

            if (!initRes.ok) throw new Error(`Init failed: ${initRes.status}`)

            const contentType = initRes.headers.get('content-type') || ''
            if (contentType.includes('application/json')) {
                const json = await initRes.json()
                const initMsg = json.message || json.response || ''
                if (initMsg) {
                    setMessages([{ id: `init-${Date.now()}`, role: 'assistant', content: initMsg, created_at: new Date().toISOString() }])
                }
            } else {
                // SSE streaming
                setIsStreaming(true)
                isStreamingRef.current = true
                const initMsgId = `init-stream-${Date.now()}`
                setMessages([{ id: initMsgId, role: 'assistant', content: '', created_at: new Date().toISOString() }])
                streamBufferRef.current = ''
                const reader = initRes.body!.getReader()
                const decoder = new TextDecoder()
                let sseBuffer = ''
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    const combined = sseBuffer + decoder.decode(value, { stream: true })
                    const lines = combined.split('\n')
                    sseBuffer = lines.pop() || ''
                    for (const line of lines) {
                        const trimmed = line.trim()
                        if (!trimmed.startsWith('data: ')) continue
                        const data = trimmed.slice(6)
                        if (data === '[DONE]') continue
                        try {
                            const parsed = JSON.parse(data)
                            if (parsed.content) {
                                streamBufferRef.current += parsed.content
                                const display = streamBufferRef.current.split('EDU_UPDATE')[0].trimEnd()
                                setMessages(prev => prev.map(m => m.id === initMsgId ? { ...m, content: display } : m))
                            }
                            if (parsed.done) {
                                const eu = parsed.eduUpdate as EduUpdate | undefined
                                setSuggestedChips(eu?.suggested_chips || [])
                                if (typeof eu?.current_concept_index === 'number') {
                                    setCurrentConceptIndex(eu.current_concept_index)
                                }
                            }
                        } catch { /* skip */ }
                    }
                }
                if (sseBuffer.trim().startsWith('data: ')) {
                    const data = sseBuffer.trim().slice(6)
                    if (data !== '[DONE]') {
                        try {
                            const parsed = JSON.parse(data)
                            if (parsed.content) {
                                streamBufferRef.current += parsed.content
                                const display = streamBufferRef.current.split('EDU_UPDATE')[0].trimEnd()
                                setMessages(prev => prev.map(m => m.id === initMsgId ? { ...m, content: display } : m))
                            }
                            if (parsed.done) {
                                const eu = parsed.eduUpdate as EduUpdate | undefined
                                setSuggestedChips(eu?.suggested_chips || [])
                                if (typeof eu?.current_concept_index === 'number') {
                                    setCurrentConceptIndex(eu.current_concept_index)
                                }
                            }
                        } catch { /* skip */ }
                    }
                }
                setIsStreaming(false)
                isStreamingRef.current = false
            }
        } catch (err) {
            setError((err as Error).message || 'Failed to start session')
        } finally {
            setIsLoading(false)
            isLoadingRef.current = false
        }
    }, [user, classId, moduleId, lessonId, categoryId, classInfo.name, lesson?.title, lesson?.learning_objective, lesson?.core_concepts, age])

    useEffect(() => {
        if (categoryId) initialise()
    }, [categoryId, initialise])

    // ── Send message ──────────────────────────────────────────────────────────
    const sendMessage = useCallback(async (text: string) => {
        if (!user || !conversationId || isLoadingRef.current || isStreamingRef.current || !categoryId) return
        if (!text.trim()) return

        const userMsg: EduMessage = { id: `u-${Date.now()}`, role: 'user', content: text.trim(), created_at: new Date().toISOString() }
        setMessages(prev => [...prev, userMsg])
        setIsLoading(true)
        isLoadingRef.current = true
        setSuggestedChips([])
        setSessionComplete(false)
        setError(null)

        abortRef.current?.abort()
        abortRef.current = new AbortController()
        streamBufferRef.current = ''

        try {
            const { data: { session: authSession } } = await supabase.auth.getSession()
            if (!authSession?.access_token) throw new Error('Not authenticated')
            const supabaseUrl = (supabase as any).supabaseUrl as string

            const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    message: text.trim(),
                    category_id: categoryId,
                    class_id: classId,
                    session_type: 'education',
                    university_mode: false,
                    current_module: moduleId,
                    current_lesson: lessonId,
                    lesson_title: lesson?.title,
                    lesson_objective: lesson?.learning_objective,
                    core_concepts: lesson?.core_concepts,
                    user_age: age,
                    language: i18n.language,
                }),
                signal: abortRef.current.signal,
            })

            if (!response.ok) throw new Error(`AI service error: ${response.status}`)

            const ct = response.headers.get('content-type') || ''
            if (ct.includes('application/json')) {
                const json = await response.json()
                if (json.type === 'quiz' && json.quiz) {
                    setIsLoading(false)
                    isLoadingRef.current = false
                    setQuiz(json.quiz)
                    setIsQuizOpen(true)
                    return
                }
                if (json.message || json.response) {
                    setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: json.message || json.response, created_at: new Date().toISOString() }])
                }
                setIsLoading(false)
                isLoadingRef.current = false
                return
            }

            // SSE streaming
            setIsLoading(false)
            isLoadingRef.current = false
            setIsStreaming(true)
            isStreamingRef.current = true

            const assistantId = `a-stream-${Date.now()}`
            setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', created_at: new Date().toISOString() }])

            const reader = response.body!.getReader()
            const decoder = new TextDecoder()
            let sseBuffer = ''
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                const combined = sseBuffer + decoder.decode(value, { stream: true })
                const lines = combined.split('\n')
                sseBuffer = lines.pop() || ''
                for (const line of lines) {
                    const trimmed = line.trim()
                    if (!trimmed.startsWith('data: ')) continue
                    const data = trimmed.slice(6)
                    if (data === '[DONE]') continue
                    try {
                        const parsed = JSON.parse(data)
                        if (parsed.content) {
                            streamBufferRef.current += parsed.content
                            const display = streamBufferRef.current.split('EDU_UPDATE')[0].trimEnd()
                            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: display } : m))
                        }
                        if (parsed.done) {
                            const eu = parsed.eduUpdate as EduUpdate | undefined
                            setSessionComplete(eu?.lesson_complete ?? false)
                            setSuggestedChips(eu?.suggested_chips || [])

                            if (typeof eu?.current_concept_index === 'number') {
                                setCurrentConceptIndex(eu.current_concept_index)
                            }
                            // Refresh mastery from DB
                            if (eu?.lesson_complete || eu?.new_current_lesson) {
                                supabase.from('class_enrollments').select('mastery_score').eq('student_id', user.id).eq('class_id', classId).maybeSingle()
                                    .then(({ data }) => { if (data) setMasteryScore(data.mastery_score || 0) })
                                setCompletedLessons(prev => ({ ...prev, [`${moduleId}-${lessonId}`]: true }))
                            }
                        }
                    } catch { /* skip */ }
                }
            }
            if (sseBuffer.trim().startsWith('data: ')) {
                const data = sseBuffer.trim().slice(6)
                if (data !== '[DONE]') {
                    try {
                        const parsed = JSON.parse(data)
                        if (parsed.content) {
                            streamBufferRef.current += parsed.content
                            const display = streamBufferRef.current.split('EDU_UPDATE')[0].trimEnd()
                            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: display } : m))
                        }
                        if (parsed.done) {
                            const eu = parsed.eduUpdate as EduUpdate | undefined
                            setSessionComplete(eu?.lesson_complete ?? false)
                            setSuggestedChips(eu?.suggested_chips || [])
                            if (typeof eu?.current_concept_index === 'number') {
                                setCurrentConceptIndex(eu.current_concept_index)
                            }
                            if (eu?.lesson_complete || eu?.new_current_lesson) {
                                supabase.from('class_enrollments').select('mastery_score').eq('student_id', user.id).eq('class_id', classId).maybeSingle()
                                    .then(({ data }) => { if (data) setMasteryScore(data.mastery_score || 0) })
                                setCompletedLessons(prev => ({ ...prev, [`${moduleId}-${lessonId}`]: true }))
                            }
                        }
                    } catch { /* skip */ }
                }
            }
        } catch (err) {
            if ((err as Error).name === 'AbortError') return
            setError((err as Error).message || 'Failed to send message')
            setIsLoading(false)
            isLoadingRef.current = false
        } finally {
            setIsStreaming(false)
            isStreamingRef.current = false
        }
    }, [user, conversationId, classId, moduleId, lessonId, categoryId, lesson?.title, lesson?.learning_objective, lesson?.core_concepts, age])

    // ── Request quiz ──────────────────────────────────────────────────────────
    const requestQuiz = useCallback(async () => {
        if (!user || !conversationId || isLoadingRef.current || !categoryId) return
        setQuizLoading(true)
        try {
            const { data: { session: authSession } } = await supabase.auth.getSession()
            if (!authSession?.access_token) throw new Error('Not authenticated')
            const supabaseUrl = (supabase as any).supabaseUrl as string
            const res = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    message: '[quiz_request]',
                    category_id: categoryId,
                    class_id: classId,
                    session_type: 'education',
                    university_mode: false,
                    current_module: moduleId,
                    current_lesson: lessonId,
                    lesson_title: lesson?.title,
                    lesson_objective: lesson?.learning_objective,
                    core_concepts: lesson?.core_concepts,
                    user_age: age,
                    language: i18n.language,
                }),
            })
            if (!res.ok) throw new Error('Quiz request failed')
            const json = await res.json()
            if (json.type === 'quiz' && json.quiz) {
                setQuiz(json.quiz)
                setIsQuizOpen(true)
                setSessionComplete(false)
            }
        } catch (err) {
            setError((err as Error).message)
        } finally {
            setQuizLoading(false)
        }
    }, [user, conversationId, classId, categoryId, moduleId, lessonId, lesson?.title, lesson?.learning_objective, lesson?.core_concepts, age])

    // ── Submit quiz ───────────────────────────────────────────────────────────
    const submitQuiz = useCallback(async (answers: Record<string, number>) => {
        if (!user || !conversationId || !quiz || !categoryId) return
        setIsQuizOpen(false)
        setIsLoading(true)
        isLoadingRef.current = true
        try {
            const { data: { session: authSession } } = await supabase.auth.getSession()
            if (!authSession?.access_token) throw new Error('Not authenticated')
            const supabaseUrl = (supabase as any).supabaseUrl as string
            const res = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    message: '[quiz_submission]',
                    category_id: categoryId,
                    class_id: classId,
                    session_type: 'education',
                    university_mode: false,
                    current_module: moduleId,
                    current_lesson: lessonId,
                    lesson_title: lesson?.title,
                    lesson_objective: lesson?.learning_objective,
                    core_concepts: lesson?.core_concepts,
                    quiz_answers: answers,
                    user_age: age,
                    language: i18n.language,
                }),
            })
            const result = await res.json()
            
            // Format review concepts if present
            let content = result.message || t('church.session.quiz.complete', { score: result.score ?? '?' })
            if (result.review && Array.isArray(result.review) && result.review.length > 0) {
                const reviewLines = result.review.map((item: any, idx: number) => {
                    const status = item.correct_yn ? '✅' : '❌'
                    let line = `${idx + 1}. ${status} **${item.question || 'Question'}**\n   - Your answer: *${item.student_answer || 'No answer'}*`
                    if (!item.correct_yn && item.correct) {
                        line += `\n   - Correct answer: *${item.correct}*`
                    }
                    if (item.explanation) {
                        line += `\n   - Explanation: ${item.explanation}`
                    }
                    return line
                }).join('\n\n')
                content += `\n\n### Review\n\n${reviewLines}`
            }

            const resultMsg: EduMessage = {
                id: `quiz-result-${Date.now()}`,
                role: 'assistant',
                content,
                created_at: new Date().toISOString(),
                metadata: { quiz_result: result },
            }
            setMessages(prev => [...prev, resultMsg])

            if (result.new_mastery_score) setMasteryScore(result.new_mastery_score)
            setQuiz(null)
            setIsQuizOpen(false)

            // Track quiz pass state
            if (result.passed) {
                setQuizPassed(true)
                setCompletedLessons(prev => ({ ...prev, [`${moduleId}-${lessonId}`]: true }))
            }

            // Automate weak concepts review if score is low (< 70%)
            const quizScore = result.score ?? result.score_percentage
            const weakAreas = result.weak_concepts || result.details?.filter((d: any) => !d.correct).map((d: any) => d.concept) || []
            if (quizScore < 70 && weakAreas.length > 0) {
                void sendMessage(`[quiz_review]::${JSON.stringify({
                    score: quizScore,
                    weak_concepts: weakAreas
                })}`)
            }

            if (result.lesson_complete || result.passed) setSessionComplete(true)

            // Fire ai-sage-overseer in background to update learner profile
            if (result.passed && user?.id) {
                supabase.functions.invoke('ai-sage-overseer', {
                    body: {
                        user_id: user.id,
                        trigger: 'lesson_complete',
                        trigger_slug: classInfo?.name || classId,
                        sage_source: 'church',
                    },
                }).catch(() => { }) // fire and forget

                // Refresh student_subject_progress if exists
                supabase.from('student_subject_progress')
                    .select('mastery_score')
                    .eq('user_id', user.id)
                    .eq('class_id', classId)
                    .maybeSingle()
                    .then(({ data }) => {
                        if (data?.mastery_score) setMasteryScore(data.mastery_score)
                    })

                // Show module milestone or B4b course completion upgrade prompt
                const currLesIdx = mod?.lessons ? mod.lessons.findIndex(l => l.lesson_id === lessonId) : -1
                const isModEnd = (mod && currLesIdx >= 0 && currLesIdx === mod.lessons.length - 1) || !!result.module_completed
                const currModIdx = classInfo.module_plan.findIndex(m => m.module_id === moduleId)
                const isCourseFinal = currModIdx === classInfo.module_plan.length - 1 && isModEnd

                // Written reflection on essay-designed courses: fires after the final lesson
                // of a module, once the quiz has passed. essay_per_module is carried on the
                // class row (classInfo).
                const isEssayModule = classInfo?.essay_mode === 'essay_per_module'
                if (result.passed && isModEnd && isEssayModule) {
                    const prompt = generateReflectionPrompt({
                        mode: classInfo?.essay_mode || 'essay_per_module',
                        subjectName: classInfo?.name,
                        moduleTitle: mod?.title,
                        moduleNumber: moduleId,
                        lessonTitle: lesson?.title,
                        lessonNumber: lessonId,
                        coreConcepts: lesson?.core_concepts || [],
                        isFaithTrack: true,
                    })
                    setReflectionPrompt(prompt)
                    setShowReflection(true)
                }

                if (isModEnd) {
                    if (isCourseFinal && isFreeCourse && !entitlements.bibleSchool) {
                        setTimeout(() => setShowUpgradePrompt(true), 900)
                    } else {
                        setTimeout(() => setShowMilestone(true), 900)
                    }
                }
            }
        } catch {
            setError(t('church.session.quiz.failedToSubmit'))
        } finally {
            setIsLoading(false)
            isLoadingRef.current = false
        }
    }, [user, conversationId, quiz, classId, categoryId, moduleId, lessonId, lesson?.title, lesson?.learning_objective, lesson?.core_concepts, age, sendMessage, isFreeCourse, entitlements.bibleSchool, classInfo.module_plan, classInfo?.essay_mode, mod])

    // ── Next lesson navigation ────────────────────────────────────────────────
    function goNextLesson() {
        setShowReflection(false)
        if (!classInfo?.module_plan?.length) return

        const currModIdx = classInfo.module_plan.findIndex(m => m.module_id === moduleId)
        const currentMod = currModIdx >= 0 ? classInfo.module_plan[currModIdx] : mod
        if (!currentMod) return

        const currLesIdx = currentMod.lessons.findIndex(l => l.lesson_id === lessonId)

        if (currLesIdx >= 0 && currLesIdx < currentMod.lessons.length - 1) {
            // Next lesson in current module
            const nextL = currentMod.lessons[currLesIdx + 1]
            navigate(`/church/session/${classId}?module=${currentMod.module_id}&lesson=${nextL.lesson_id}`)
        } else if (currModIdx >= 0 && currModIdx < classInfo.module_plan.length - 1) {
            // First lesson of next module
            const nextM = classInfo.module_plan[currModIdx + 1]
            const firstL = nextM.lessons[0]
            if (firstL) {
                navigate(`/church/session/${classId}?module=${nextM.module_id}&lesson=${firstL.lesson_id}`)
            } else {
                navigate('/church/learn')
            }
        } else {
            // Course complete
            navigate('/church/learn')
        }
    }

    const lessonMastery = lesson?.core_concepts && lesson.core_concepts.length > 0
        ? Math.round((currentConceptIndex / lesson.core_concepts.length) * 100)
        : 0

    const currentModIndex = classInfo.module_plan.findIndex(m => m.module_id === moduleId)
    const currentLesIndex = mod ? mod.lessons.findIndex(l => l.lesson_id === lessonId) : -1

    const isLastLesson = mod ? (currentLesIndex >= 0 && currentLesIndex === mod.lessons.length - 1) : false
    const isLastModule = currentModIndex >= 0 && currentModIndex === classInfo.module_plan.length - 1
    const isCapstone = isLastLesson && isLastModule
    const completedCount = Object.keys(completedLessons).length
    const progressPct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0

    function handleSend() {
        const text = input.trim()
        if (!text || isLoading || isStreaming) return
        setInput('')
        // Intercept quiz-related text → trigger proper QuizPanel instead of AI text
        if (/\b(start|take|launch|begin|do|ready for|give me)\s*(the\s+)?quiz\b/i.test(text)) {
            requestQuiz()
            return
        }
        sendMessage(text)
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
    }

    return (
        <div style={{ display: 'flex', height: '100dvh', background: DARK2, overflow: 'hidden', fontFamily: "'Inter', system-ui, sans-serif" }}>
            <style>{`
        .prose-church p:last-child { margin-bottom: 0; }
        .prose-church ul, .prose-church ol { margin-top: 0.2em; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            {/* ── Mobile Sidebar Overlay ── */}
            <AnimatePresence>
                {isMobile && showSidebar && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setShowSidebar(false)}
                        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100, backdropFilter: 'blur(4px)' }} />
                )}
            </AnimatePresence>

            {/* ── Sidebar ── */}
            <motion.div
                animate={{ x: isMobile ? (showSidebar ? 0 : -280) : 0, width: isMobile ? 280 : 260 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                style={{ position: isMobile ? 'absolute' : 'relative', height: '100%', flexShrink: 0, borderRight: `1px solid ${BR}`, background: isMobile ? '#0d1828' : 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 101 }}
            >
                {/* Sidebar header */}
                <div style={{ padding: '22px 18px 14px', borderBottom: `1px solid ${BR}` }}>
                    <button onClick={() => navigate('/church/learn')} style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontSize: 11, padding: 0, marginBottom: 10, fontWeight: 700, letterSpacing: '0.5px' }}>
                        ← {t('church.session.backToCourses')}
                    </button>
                    <p style={{ fontSize: 11, color: GOLD, fontWeight: 800, letterSpacing: '1.5px', marginBottom: 5, textTransform: 'uppercase' }}>
                        {t('church.session.lesson', {
                            n: mod ? mod.lessons.findIndex(l => l.lesson_id === lessonId) + 1 : lessonId
                        })}
                    </p>
                    <p style={{ fontSize: 14, fontWeight: 700, color: TX, lineHeight: 1.35 }}>{lesson?.title || 'Loading...'}</p>
                </div>

                {/* Sidebar scrollable content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }} className="scrollbar-hide">
                    {/* Progress widget */}
                    <div style={{ background: 'rgba(200,168,75,0.06)', borderRadius: 14, padding: '12px 14px', marginBottom: 18, border: '1px solid rgba(200,168,75,0.12)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 0.5 }}>
                                {t('church.session.progress')}
                            </span>
                            <span style={{ fontSize: 10, fontWeight: 800, color: GOLD }}>{progressPct}%</span>
                        </div>
                        <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
                            <motion.div initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.8 }}
                                style={{ height: '100%', background: GOLD }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: MU }}>
                            <span>{t('church.session.mastery')}: <strong style={{ color: GOLD }}>{lessonMastery}%</strong></span>
                            <span>{completedCount}/{totalLessons} {t('church.session.lessonsComplete')}</span>
                        </div>

                        {lesson?.core_concepts && lesson.core_concepts.length > 0 && (
                            <div style={{ marginTop: 14 }}>
                                <p style={{ fontSize: 9, fontWeight: 800, color: MU, marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
                                    {t('church.session.coreConcepts')}
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {lesson.core_concepts.map((concept, idx) => {
                                        const isDone = currentConceptIndex > idx
                                        const isCurrent = currentConceptIndex === idx
                                        return (
                                            <div key={idx} style={{
                                                padding: '9px 12px',
                                                borderRadius: 10,
                                                background: isCurrent ? 'rgba(200,168,75,0.12)' : isDone ? 'rgba(74,222,128,0.04)' : CA,
                                                border: `1px solid ${isCurrent ? GOLD : isDone ? 'rgba(74,222,128,0.15)' : BR}`,
                                                fontSize: 11,
                                                color: isCurrent || isDone ? TX : MU,
                                                display: 'flex',
                                                gap: 10,
                                                alignItems: 'flex-start',
                                                transition: 'all 0.3s ease'
                                            }}>
                                                <div style={{
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    background: isDone ? '#4ade80' : isCurrent ? GOLD : 'rgba(255,255,255,0.05)',
                                                    border: `1.5px solid ${isCurrent ? GOLD : isDone ? '#4ade80' : BR}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0, marginTop: 1
                                                }}>
                                                    {isDone && <span style={{ color: '#0a1220', fontSize: 8, fontWeight: 900 }}>✓</span>}
                                                    {isCurrent && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#0a1220' }} />}
                                                </div>
                                                <span style={{ lineHeight: 1.4, fontWeight: isCurrent ? 600 : 400 }}>{concept}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Module / lesson tree */}
                    {classInfo.module_plan.map(m => (
                        <div key={m.module_id} style={{ marginBottom: 4 }}>
                            <div style={{ padding: '7px 10px', fontSize: 10, fontWeight: 800, color: m.module_id === moduleId ? GOLD : MU, letterSpacing: '0.8px', textTransform: 'uppercase', lineHeight: 1.3 }}>
                                {m.title}
                            </div>
                            {/* Show lessons for all modules (collapsed by default for non-current) */}
                            {(m.module_id === moduleId || m.lessons.some(l => completedLessons[`${m.module_id}-${l.lesson_id}`])) && m.lessons.map((l, lIdx) => {
                                const done = !!completedLessons[`${m.module_id}-${l.lesson_id}`]
                                const current = m.module_id === moduleId && l.lesson_id === lessonId
                                return (
                                    <button key={l.lesson_id} onClick={() => {
                                        navigate(`/church/session/${classId}?module=${m.module_id}&lesson=${l.lesson_id}`)
                                        if (isMobile) setShowSidebar(false)
                                    }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', background: current ? 'rgba(200,168,75,0.12)' : 'none', border: 'none', cursor: 'pointer', padding: '9px 10px', borderRadius: 9, textAlign: 'left', marginBottom: 2, transition: 'background 0.15s' }}>
                                        <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, background: done ? '#4ade80' : current ? GOLD : 'rgba(255,255,255,0.07)', color: done || current ? '#0a1220' : MU }}>
                                            {done ? '✓' : lIdx + 1}
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: current ? 600 : 400, color: current ? TX : MU, lineHeight: 1.4, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {l.title}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </div>
            </motion.div>

            {/* ── Chat Area ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                {/* Top bar */}
                <div style={{ padding: isMobile ? '0 6px' : '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(10,18,32,0.85)', backdropFilter: 'blur(10px)', zIndex: 10 }}>
                    {isMobile && (
                        <button onClick={() => setShowSidebar(!showSidebar)} style={{ background: 'none', border: 'none', color: TX, cursor: 'pointer', padding: 0, marginRight: 4 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
                        </button>
                    )}
                    <ChurchIcon size={isMobile ? 26 : 30} />
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <p style={{ fontSize: 13, fontWeight: 800, color: TX, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{classInfo.name}</p>
                        <p style={{ fontSize: 10, color: MU, margin: 0, fontWeight: 600 }}>{mod?.title}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <LanguagePicker />
                    </div>
                </div>

                {/* Messages area */}
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 4px 8px' : '24px 24px 16px' }} className="scrollbar-hide">
                    {messages.length === 0 && !isLoading && (
                        <div style={{ textAlign: 'center', paddingTop: isMobile ? 40 : 60 }}>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                                <ChurchIcon size={48} />
                            </div>
                            <p style={{ color: MU, fontSize: 14, maxWidth: 260, margin: '0 auto', lineHeight: 1.6 }}>
                                {t('church.session.intro')}
                            </p>
                        </div>
                    )}

                    {messages.map(msg => (
                        <MessageBubble key={msg.id} content={msg.content} role={msg.role} isMobile={isMobile} />
                    ))}

                    {/* Typing indicator */}
                    {(isLoading || isStreaming) && (messages.length === 0 || messages[messages.length - 1]?.role === 'user') && (
                        <div style={{ display: 'flex', marginBottom: 18, gap: isMobile ? 6 : 10 }}>
                            <div style={{ marginTop: 4 }}><ChurchIcon size={isMobile ? 24 : 32} /></div>
                            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: '4px 18px 18px 18px' }}><TypingDots /></div>
                        </div>
                    )}

                    {/* Error */}
                    <AnimatePresence>
                        {error && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                style={{ background: 'rgba(248,113,113,0.09)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 12, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, color: '#f87171' }}>{error}</span>
                                <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16 }}>✕</button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Quiz loading */}
                    <AnimatePresence>
                        {quizLoading && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                style={{ background: 'rgba(200,168,75,0.08)', border: '1px solid rgba(200,168,75,0.2)', borderRadius: 14, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                                    style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(200,168,75,0.2)', borderTopColor: GOLD, flexShrink: 0 }} />
                                <span style={{ fontSize: 13, color: GOLD, fontWeight: 600 }}>
                                    {t('church.session.quizLoading')}
                                </span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Session complete / quiz CTA */}
                    <AnimatePresence>
                        {sessionComplete && (
                            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                                style={{ background: 'rgba(200,168,75,0.09)', border: '1.5px solid rgba(200,168,75,0.35)', borderRadius: 18, padding: isMobile ? '18px' : '22px', marginBottom: 14, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 14 }}>
                                <div>
                                    <p style={{ fontWeight: 800, fontSize: 16, color: GOLD, marginBottom: 3 }}>
                                        🎉 {t('church.session.lessonComplete')}
                                    </p>
                                    <p style={{ fontSize: 13, color: MU, fontWeight: 500 }}>
                                        {isCapstone
                                            ? t('church.session.courseComplete')
                                            : isLastLesson
                                                ? t('church.session.moduleComplete')
                                                : t('church.session.lessonCompleteHint')}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
                                    <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={requestQuiz}
                                        style={{ background: `linear-gradient(135deg, ${GOLD}, #9a7228)`, color: DARK, border: 'none', borderRadius: 11, padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer', flex: isMobile ? 1 : 'none', boxShadow: '0 4px 12px rgba(200,168,75,0.25)' }}>
                                        📋 {t('church.session.startQuiz')}
                                    </motion.button>
                                    {!isCapstone && quizPassed && (
                                        <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={goNextLesson}
                                            style={{ background: 'rgba(200,168,75,0.15)', color: GOLD, border: '1px solid rgba(200,168,75,0.35)', borderRadius: 11, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flex: isMobile ? 1 : 'none' }}>
                                            {isLastLesson ? t('church.session.nextModule') : t('church.session.nextLesson')} →
                                        </motion.button>
                                    )}
                                    {isCapstone && (
                                        <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => navigate('/church/learn')}
                                            style={{ background: `linear-gradient(135deg, ${GOLD}, #9a7228)`, color: DARK, border: 'none', borderRadius: 11, padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                                            {t('church.session.backToCourses')} →
                                        </motion.button>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Suggested chips — with fallback when AI doesn't provide any */}
                    {!isQuizOpen && !quizLoading && messages.length > 0 && (() => {
                        const displayChips = suggestedChips.length > 0
                            ? suggestedChips
                            : sessionComplete
                                ? [t('church.session.startQuiz'), 'Review key points', 'I have a question']
                                : quizPassed
                                    ? [t('church.session.nextLesson'), 'Review this lesson', 'I have a question']
                                    : ['Continue the lesson', 'Explain differently', 'Give me an example']
                        return (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                                {displayChips.map(chip => (
                                    <motion.button key={chip}
                                        onClick={() => {
                                            if (/\bquiz\b/i.test(chip)) {
                                                requestQuiz()
                                            } else {
                                                sendMessage(chip)
                                            }
                                        }}
                                        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                        style={{ background: 'rgba(200,168,75,0.08)', border: '1px solid rgba(200,168,75,0.22)', borderRadius: 100, padding: '8px 16px', fontSize: 13, color: '#e8c97a', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
                                        {chip}
                                    </motion.button>
                                ))}
                            </div>
                        )
                    })()}

                    {/* Written reflection on essay-designed courses */}
                    {showReflection && (
                        <div style={{ marginTop: 20, marginBottom: 24 }}>
                            <ShortAnswerBox
                                prompt={reflectionPrompt}
                                classId={classId}
                                moduleId={moduleId}
                                lessonId={lessonId}
                                sageSource="bible_college"
                                accentColor={GOLD}
                                wordMin={50}
                                wordMax={300}
                                onComplete={() => {
                                    refreshEnrolmentMastery()
                                }}
                            />
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Milestone Celebration */}
                <AnimatePresence>
                    {showMilestone && (
                        <ModuleMilestone
                            isOpen={showMilestone}
                            moduleTitle={mod?.title || `Module ${moduleId}`}
                            className={classInfo.name}
                            accentColor={GOLD}
                            variant="church"
                            dashboardPath="/church/learn"
                            onClose={() => {
                                setShowMilestone(false)
                                goNextLesson()
                            }}
                        />
                    )}
                </AnimatePresence>

                {/* B4b: Course-1 Completion Upgrade Prompt */}
                <AnimatePresence>
                    {showUpgradePrompt && (
                        <div style={{
                            position: 'fixed', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 3100, padding: 20,
                        }}>
                            {/* Backdrop */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => {
                                    setShowUpgradePrompt(false)
                                    navigate('/church/learn')
                                }}
                                style={{
                                    position: 'absolute', inset: 0,
                                    background: 'rgba(6,10,18,0.92)',
                                    backdropFilter: 'blur(16px)',
                                }}
                            />

                            {/* Modal Card */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9, y: 24 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: 24 }}
                                transition={{ type: 'spring', damping: 22, stiffness: 140 }}
                                style={{
                                    position: 'relative', width: '100%', maxWidth: 500,
                                    background: 'linear-gradient(145deg, #131d2e 0%, #0c1422 100%)',
                                    border: `1.5px solid ${GOLD}70`,
                                    borderRadius: 24, padding: isMobile ? '28px 20px' : '40px 32px',
                                    textAlign: 'center', boxShadow: '0 25px 70px rgba(0,0,0,0.65)',
                                    color: '#fff',
                                }}
                            >
                                <div style={{
                                    width: 64, height: 64, borderRadius: '50%',
                                    background: 'rgba(200,168,75,0.15)', border: `2px solid ${GOLD}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}>
                                    <Sparkles size={32} color={GOLD} />
                                </div>

                                <div style={{ fontSize: 11, fontWeight: 800, color: GOLD, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 8 }}>
                                    ✦ {t('church.session.b4b.badge', 'Course Completed')}
                                </div>

                                <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 12, lineHeight: 1.25, color: '#fff' }}>
                                    {t('church.session.b4b.title', "You've Completed Meeting Jesus First!")}
                                </h2>

                                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 24, maxWidth: 420, margin: '0 auto 24px' }}>
                                    {t('church.session.b4b.desc', 'The first step of your journey is complete. The other 30 courses in our structured Bible Journey curriculum are waiting for you.')}
                                </p>

                                <div style={{
                                    background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`,
                                    borderRadius: 16, padding: '16px', marginBottom: 24,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
                                }}>
                                    <span style={{ fontSize: 14, color: '#bbb', fontWeight: 600 }}>
                                        {t('church.session.b4b.fullAccess', 'Full Bible Journey Access')}
                                    </span>
                                    <span style={{ fontSize: 26, fontWeight: 900, color: GOLD }}>
                                        $7<span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>/mo</span>
                                    </span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={startCheckout}
                                        disabled={upgrading}
                                        style={{
                                            background: `linear-gradient(135deg, ${GOLD}, #b08d32)`,
                                            color: DARK2, border: 'none', padding: '14px 24px',
                                            borderRadius: 14, fontSize: 15, fontWeight: 900,
                                            cursor: upgrading ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 8px 24px rgba(200,168,75,0.3)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        }}
                                    >
                                        <Lock size={16} />
                                        {upgrading ? t('church.session.b4b.connecting', 'Connecting to checkout...') : t('church.session.b4b.unlockBtn', 'Unlock All 30 Courses — $7/mo')}
                                    </motion.button>

                                    <button
                                        onClick={() => {
                                            setShowUpgradePrompt(false)
                                            navigate('/church/learn')
                                        }}
                                        style={{
                                            background: 'transparent', border: 'none',
                                            color: 'rgba(255,255,255,0.5)', padding: '10px',
                                            fontSize: 13, cursor: 'pointer', fontWeight: 600,
                                        }}
                                    >
                                        {t('church.session.b4b.returnDashboard', 'Return to Bible Journey Dashboard')}
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

                {/* Quiz overlay */}
                <AnimatePresence>
                    {quiz && isQuizOpen && (
                        <QuizPanel quiz={quiz} onSubmit={submitQuiz} onDismiss={() => { setIsQuizOpen(false); setQuiz(null) }} />
                    )}
                </AnimatePresence>

                {/* Input */}
                <div style={{ padding: isMobile ? '10px 6px 16px' : '14px 24px 22px', borderTop: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(10,18,32,0.7)', backdropFilter: 'blur(20px)' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                        <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                            placeholder={isMobile ? t('church.session.placeholderMobile') : t('church.session.placeholder')}
                            rows={1}
                            style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${BR}`, borderRadius: 14, padding: isMobile ? '11px 14px' : '13px 16px', color: TX, fontSize: 14, outline: 'none', resize: 'none', fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.5, maxHeight: 120, transition: 'border-color 0.2s' }}
                            onFocus={e => (e.target.style.borderColor = 'rgba(200,168,75,0.5)')}
                            onBlur={e => (e.target.style.borderColor = BR)} />
                        <motion.button whileHover={!isMobile ? { scale: 1.05 } : {}} whileTap={{ scale: 0.92 }} onClick={handleSend}
                            disabled={!input.trim() || isLoading || isStreaming}
                            style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: input.trim() && !isLoading && !isStreaming ? `linear-gradient(135deg, ${GOLD}, #9a7228)` : 'rgba(255,255,255,0.05)', border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed', color: input.trim() ? DARK : MU, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>
                        </motion.button>
                    </div>
                    {!isMobile && (
                        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.18)', textAlign: 'center', marginTop: 8, fontWeight: 500 }}>
                            {t('church.session.footerHint')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── ChurchSessionPage (outer) ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export default function ChurchSessionPage() {
    const { t } = useTranslation()
    const { classId } = useParams<{ classId: string }>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { user, isLoading: authLoading } = useAuth()

    // moduleId and lessonId are initially parsed from URL, but we'll validate/default them after classInfo is loaded
    const [moduleId, setModuleId] = useState<number | null>(null)
    const [lessonId, setLessonId] = useState<number | null>(null)

    const [classInfo, setClassInfo] = useState<ClassInfo | null>(null)
    const [age, setAge] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!authLoading && !user) {
            const currentPath = `/church/session/${classId}${window.location.search || ''}`
            try {
                localStorage.setItem('sage_faith_intent', currentPath)
                sessionStorage.setItem('sage_faith_intent', currentPath)
            } catch { }
            navigate(`/signin?next=${encodeURIComponent(currentPath)}`, { replace: true })
        }
    }, [user, authLoading, navigate, classId])

    useEffect(() => {
        if (!user || !classId) return
        let cancelled = false

        async function load() {
            try {
                // Fetch class info
                const { data: cls } = await supabase.from('classes')
                    .select('id, name, category_id, module_plan, description, essay_mode')
                    .eq('id', classId as string)
                    .single()

                // Fetch user age
                const { data: profile } = await supabase.from('users')
                    .select('age')
                    .eq('id', user!.id)
                    .single()

                if (!cancelled) {
                    if (cls) {
                        const info = cls as unknown as ClassInfo
                        setClassInfo(info)

                        // Determine current module/lesson from URL or default to first in plan
                        const urlMod = parseInt(searchParams.get('module') || '0')
                        const urlLes = parseInt(searchParams.get('lesson') || '0')

                        // Check if the URL IDs exist in the plan
                        const modExists = info.module_plan.find(m => m.module_id === urlMod)
                        const lesExists = modExists?.lessons.find(l => l.lesson_id === urlLes)

                        if (modExists && lesExists) {
                            setModuleId(urlMod)
                            setLessonId(urlLes)
                        } else {
                            // Default to first module and first lesson in that module
                            const firstMod = info.module_plan[0]
                            const firstLes = firstMod?.lessons[0]
                            setModuleId(firstMod?.module_id || 0)
                            setLessonId(firstLes?.lesson_id || 0)
                        }
                    }
                    if (profile?.age) setAge(profile.age)
                    setLoading(false)
                }
            } catch (err) {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => { cancelled = true }
    }, [user, classId, searchParams])

    if (authLoading || (user && loading) || !moduleId || !lessonId) {
        return (
            <div style={{ background: DARK2, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 38, height: 38, borderRadius: '50%', border: `3px solid rgba(200,168,75,0.15)`, borderTopColor: GOLD }} />
                <p style={{ color: GOLD, fontSize: 13, fontFamily: 'sans-serif' }}>{t('church.session.loading')}</p>
            </div>
        )
    }

    if (!classInfo) {
        return (
            <div style={{ background: DARK2, height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: TX }}>
                <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t('church.session.notFound')}</p>
                <button onClick={() => navigate('/church/learn')} style={{ background: GOLD, color: DARK2, border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                    {t('church.session.back')}
                </button>
            </div>
        )
    }

    return <ChurchSessionInner
        key={`${classId}-${moduleId}-${lessonId}`}
        classId={classId!}
        classInfo={classInfo}
        moduleId={moduleId}
        lessonId={lessonId}
        age={age}
        categoryId={classInfo.category_id}
    />
}
